import React, { useEffect, useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import Modal from "react-modal";
import { toast } from "react-toastify";
import api from "../../api";
import CalendarTab from "./CalendarTab";
import AppointmentList from "./AppointmentList";
import AppointmentModal from "./AppointmentModal";
import { mapAppointmentToEvent, mapMissionToEvent } from "./utils";
import MissionInfoModal from "./MissionInfoModal";
import { useTranslation } from "react-i18next";
import { FaCalendarAlt } from "react-icons/fa";
import "react-tabs/style/react-tabs.css";

Modal.setAppElement("#root");

const customTabsStyle = `
.react-tabs__tab-list {
  border-bottom: none !important;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
  margin: 0 0 1.5rem 0 !important;
  background: transparent;
}
.react-tabs__tab {
  background: transparent;
  border: 1px solid oklch(var(--b3));
  border-radius: 0.5rem;
  color: oklch(var(--bc) / 0.6);
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, color .15s, border-color .15s;
  outline: none;
  box-shadow: none;
}
.react-tabs__tab--selected {
  background: oklch(var(--p));
  color: oklch(var(--pc));
  border-color: oklch(var(--p));
  box-shadow: none;
}
.react-tabs__tab:focus {
  box-shadow: none;
}
`;

const CalendarView = () => {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [missions, setMissions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [missionInfoTarget, setMissionInfoTarget] = useState(null);
  const [appointmentWaybillMissionId, setAppointmentWaybillMissionId] = useState(null);

  const [form, setForm] = useState({
    client: "",
    address: "",
    phone: "",
    type: "Moving",
    date: "",
    time: "",
    note: "",
    email: "",
    status: "confirmed",
  });

  // Inject custom styles for tabs (1 seule fois)
  useEffect(() => {
    let style = document.getElementById("calendarview-tabs-style");
    if (!style) {
      style = document.createElement("style");
      style.id = "calendarview-tabs-style";
      style.innerHTML = customTabsStyle;
      document.head.appendChild(style);
    }
  }, []);

  // Missions synchronisées sur le calendrier (lecture seule) : vision claire des
  // missions prévues sans quitter le calendrier. Best-effort — un échec n'empêche
  // pas d'afficher les rendez-vous.
  const fetchMissions = async () => {
    try {
      const res = await api.get("/missions?limit=100");
      setMissions(res.data?.data || []);
    } catch {
      setMissions([]);
    }
  };

  useEffect(() => { fetchMissions(); }, []);

  const fetchAppointments = async () => {
    try {
      const res = await api.get("/appointments");
      setAppointments(res.data);
    } catch (err) {
      console.error("❌ Error fetching appointments:", err);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const openModalForNew = (dateStr) => {
    setForm({
      client: "",
      address: "",
      phone: "",
      type: "Moving",
      date: dateStr,
      time: "",
      note: "",
      email: "",
      status: "confirmed",
    });
    setSelectedEvent(null);
    setAppointmentWaybillMissionId(null);
    setModalOpen(true);
  };

  const handleWaybillDownload = async (missionId) => {
    try {
      const r = await api.get(`/waybill/${missionId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
    } catch {
      toast.error(t("mission.list.waybillError"));
    }
  };

  const openModalForEdit = (arg) => {
    const e = arg.event || arg;
    if (!e.start) return;

    // Mission (lecture seule) : panneau d'info dédié, ne touche pas au flux RDV.
    if (e.extendedProps?.kind === "mission") {
      setMissionInfoTarget(e.extendedProps);
      return;
    }

    try {
      const dateObj = new Date(e.start);
      const datePart = dateObj.toISOString().split("T")[0];
      const timePart = dateObj.toTimeString().split(":").slice(0, 2).join(":");

      setForm({
        client: e.extendedProps?.client || "",
        address: e.extendedProps?.address || "",
        phone: e.extendedProps?.phone || "",
        type: e.extendedProps?.type || "Moving",
        date: datePart,
        time: timePart,
        note: e.extendedProps?.note || "",
        email: e.extendedProps?.email || "",
        status: e.extendedProps?.status || "confirmed",
      });

      setSelectedEvent(e);
      // LDV récupérable depuis le RDV seulement si une mission partage son quote_id.
      const quoteId = e.extendedProps?.quoteId;
      const linkedMission = quoteId ? missions.find((m) => m.quote_id === quoteId) : null;
      setAppointmentWaybillMissionId(linkedMission?.id || null);
      setModalOpen(true);
    } catch (error) {
      console.error("❌ Failed to parse event date:", e.start, error);
    }
  };

  const handleSave = async () => {
    if (!form.date) {
      window.alert(t("calendar.dateRequired", "Veuillez choisir une date."));
      return;
    }
    const payload = {
      ...form,
      // time optionnel : sans heure on garde une date "all-day" parseable
      date: form.time ? `${form.date}T${form.time}` : form.date,
    };

    try {
      if (selectedEvent) {
        await api.put(`/appointments/${selectedEvent.id}`, payload);
      } else {
        await api.post("/appointments", payload);
      }
      setModalOpen(false);
      fetchAppointments();
    } catch (err) {
      console.error("❌ Error saving:", err);
    }
  };

  const handleDelete = async (arg) => {
    // Depuis la liste on reçoit l'event en argument ; depuis le modal on prend selectedEvent.
    const target = (arg && arg.id) ? arg : selectedEvent;
    if (!target) return;
    if (!window.confirm(t("calendar.confirmDelete", "Supprimer ce rendez-vous ?"))) return;
    try {
      await api.delete(`/appointments/${target.id}`);
      setModalOpen(false);
      fetchAppointments();
    } catch (err) {
      console.error("❌ Error deleting appointment:", err);
    }
  };

  const events = appointments.map(mapAppointmentToEvent);
  // Vue calendrier (mois/semaine/jour) uniquement : RDV + missions fusionnés.
  // La liste (onglet Liste) reste RDV uniquement, comportement inchangé.
  const calendarEvents = [...events, ...missions.map(mapMissionToEvent)];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 text-primary">
            <FaCalendarAlt size={18} />
          </span>
          <h1 className="text-xl font-bold text-base-content">{t("calendar.title")}</h1>
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => openModalForNew("")}
        >
          + {t("calendar.new")}
        </button>
      </div>

      {/* Calendar card */}
      <div className="bg-base-100 rounded-xl border border-base-200 shadow-sm p-4">
        <Tabs>
          <TabList>
            <Tab>{t("calendar.tab.calendar")}</Tab>
            <Tab>{t("calendar.tab.list")}</Tab>
          </TabList>

          <TabPanel>
            <CalendarTab
              events={calendarEvents}
              onDateClick={openModalForNew}
              onEventClick={openModalForEdit}
            />
          </TabPanel>

          <TabPanel>
            <AppointmentList
              events={events}
              onEdit={openModalForEdit}
              onDelete={handleDelete}
            />
          </TabPanel>
        </Tabs>
      </div>

      <AppointmentModal
        isOpen={modalOpen}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        onClose={() => setModalOpen(false)}
        onDelete={handleDelete}
        isEdit={!!selectedEvent}
        waybillMissionId={appointmentWaybillMissionId}
        onDownloadWaybill={handleWaybillDownload}
      />

      <MissionInfoModal
        mission={missionInfoTarget}
        onClose={() => setMissionInfoTarget(null)}
        onDownloadWaybill={handleWaybillDownload}
      />
    </div>
  );
};

export default CalendarView;
